// frontend/src/pages/admin/AdminGiftCards.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { giftCards as giftCardsApi } from '../../utils/api';
import { useAuth } from '../../utils/auth';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Gift, Copy, Trash2, CheckCircle, Download, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const STATUS_LABEL = { active:'Actif', used:'Utilisé', expired:'Expiré', cancelled:'Annulé' };
const STATUS_COLOR = { active:'var(--teal)', used:'var(--text-3)', expired:'var(--amber)', cancelled:'var(--red)' };


function BulkModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ quantity:'10', amount:'25', message:'' });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!form.quantity || !form.amount) return toast.error('Champs requis');
    setSaving(true);
    try {
      const r = await giftCardsApi.bulk(form);
      setResult(r.data.giftCards);
      toast.success(`${r.data.count} bons générés`);
      onCreated();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const exportCSV = () => {
    if (!result) return;
    const header = 'Code,Montant,Expire le';
    const rows = result.map(gc =>
      `${gc.code},${parseFloat(gc.initial_amount).toFixed(2)},${format(new Date(gc.expires_at),'dd/MM/yyyy',{locale:fr})}`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bons-cadeaux-${format(new Date(),'yyyy-MM-dd')}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:460}}>
        <h3 className="modal-title">Génération en lot</h3>
        {!result ? (
          <>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Quantité (max 100) *</label>
                <input type="number" min="1" max="100" className="input"
                  value={form.quantity} onChange={e=>setForm(p=>({...p,quantity:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label className="form-label">Montant unitaire (€) *</label>
                <input type="number" step="0.01" min="1" className="input"
                  value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))}/>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Message (optionnel)</label>
              <input className="input" value={form.message}
                onChange={e=>setForm(p=>({...p,message:e.target.value}))}
                placeholder="Offert par Game Park"/>
            </div>
            <div style={{padding:'10px 14px', background:'var(--bg-elevated)', borderRadius:8,
              fontSize:12, color:'var(--text-3)', marginBottom:16}}>
              Valeur totale : <strong>{(parseInt(form.quantity||0)*parseFloat(form.amount||0)).toFixed(2)} €</strong>
              · Validité : 1 an
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
              <button className="btn btn-primary" onClick={submit} disabled={saving}>
                {saving ? <span className="spinner" style={{width:14,height:14}}/> : <><Gift size={14}/> Générer</>}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{maxHeight:300, overflowY:'auto', marginBottom:16}}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Code</th><th className="text-right">Montant</th></tr></thead>
                  <tbody>
                    {result.map(gc => (
                      <tr key={gc.code}>
                        <td style={{fontFamily:'monospace', fontWeight:700, color:'var(--teal)'}}>{gc.code}</td>
                        <td className="text-right bold">{parseFloat(gc.initial_amount).toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
              <button className="btn btn-primary" onClick={exportCSV}>
                <Download size={14}/> Exporter CSV
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NewGiftCardModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ amount:'25', purchasedByEmail:'', purchasedByName:'', message:'' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Montant requis');
    setSaving(true);
    try {
      const r = await giftCardsApi.create(form);
      toast.success(`Bon créé : ${r.data.giftCard.code}`);
      onCreated(r.data.giftCard);
      onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:460}}>
        <h3 className="modal-title">Nouveau bon cadeau</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Montant (€) *</label>
            <input type="number" step="0.01" min="1" className="input"
              value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))}/>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Bénéficiaire (nom)</label>
            <input className="input" value={form.purchasedByName}
              onChange={e=>setForm(p=>({...p,purchasedByName:e.target.value}))}
              placeholder="Jean Dupont"/>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input type="email" className="input" value={form.purchasedByEmail}
              onChange={e=>setForm(p=>({...p,purchasedByEmail:e.target.value}))}
              placeholder="jean@exemple.fr"/>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Message personnalisé</label>
          <textarea className="input" rows={2} value={form.message}
            onChange={e=>setForm(p=>({...p,message:e.target.value}))}
            placeholder="Joyeux anniversaire !" style={{resize:'vertical'}}/>
        </div>
        <div style={{padding:'10px 14px', background:'var(--bg-elevated)', borderRadius:8,
          fontSize:12, color:'var(--text-3)', marginBottom:16}}>
          📅 Valable 1 an à partir d'aujourd'hui
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? <span className="spinner" style={{width:14,height:14}}/> : <><Gift size={14}/> Créer le bon</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatedModal({ giftCard, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(giftCard.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:400, textAlign:'center'}}>
        <div style={{fontSize:48, marginBottom:12}}>🎁</div>
        <h3 style={{margin:'0 0 8px'}}>Bon cadeau créé !</h3>
        <p style={{color:'var(--text-3)', fontSize:13, marginBottom:20}}>
          Valable jusqu'au {format(new Date(giftCard.expires_at), 'd MMMM yyyy', {locale:fr})}
        </p>
        <div style={{display:'flex', alignItems:'center', gap:10, padding:'16px 20px',
          background:'var(--bg-elevated)', borderRadius:12, marginBottom:20}}>
          <span style={{flex:1, fontFamily:'monospace', fontSize:22, fontWeight:800,
            letterSpacing:'0.1em', color:'var(--teal)'}}>
            {giftCard.code}
          </span>
          <button onClick={copy} style={{background:'none', border:'none', cursor:'pointer',
            color: copied ? 'var(--teal)' : 'var(--text-3)'}}>
            {copied ? <CheckCircle size={18}/> : <Copy size={18}/>}
          </button>
        </div>
        <div style={{fontSize:18, fontWeight:700, color:'var(--text-1)', marginBottom:20}}>
          {parseFloat(giftCard.initial_amount).toFixed(2)} €
        </div>
        <button className="btn btn-primary w-full" style={{justifyContent:'center'}} onClick={onClose}>
          Fermer
        </button>
      </div>
    </div>
  );
}

export default function AdminGiftCards() {
  const { can } = useAuth();
  if (!can('staff')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [cards, setCards]         = useState([]);
  const [showNew, setShowNew]     = useState(false);
  const [showBulk, setShowBulk]   = useState(false);
  const [created, setCreated]     = useState(null);
  const [filter, setFilter]       = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'gift' | 'avoir'
  const navigate = useNavigate();
  const [confirmAction, setConfirmAction] = useState(null); // { type:'cancelCard', id } | { type:'cancelUsage', usageId }
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = () => giftCardsApi.list().then(r => setCards(r.data.giftCards || []));
  useEffect(() => { load(); }, []);

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    setConfirmBusy(true);
    try {
      if (confirmAction.type === 'cancelCard') {
        await giftCardsApi.cancel(confirmAction.id);
        toast.success('Bon annulé');
      } else if (confirmAction.type === 'cancelUsage') {
        await giftCardsApi.cancelUsage(confirmAction.usageId);
        toast.success('Usage annulé');
      }
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setConfirmBusy(false); setConfirmAction(null); }
  };

  const filtered = cards
    .filter(c => filter === 'all' || c.status === filter)
    .filter(c => typeFilter === 'all' || (c.gift_type || 'gift') === typeFilter);

  return (
    <>
      <div className="page-header">
        <div><h2>Bons cadeaux</h2><p>Génération et suivi des bons cadeaux</p></div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-secondary" onClick={() => setShowBulk(true)}>
            <Gift size={15}/> Générer en lot
          </button>
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={15}/> Nouveau bon
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Stats rapides */}
        <div style={{display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:20}}>
          {[
            ['Total créés', cards.length, 'var(--text-1)'],
            ['Bons actifs', cards.filter(c=>c.status==='active'&&(c.gift_type||'gift')==='gift').length, 'var(--teal)'],
            ['Avoirs actifs', cards.filter(c=>c.status==='active'&&c.gift_type==='avoir').length, 'var(--amber)'],
            ['Utilisés', cards.filter(c=>c.status==='used').length, 'var(--text-3)'],
            ['Valeur active', cards.filter(c=>c.status==='active').reduce((s,c)=>s+parseFloat(c.remaining_amount||0),0).toFixed(2)+' €', 'var(--accent-light)'],
          ].map(([label, val, color]) => (
            <div key={label} className="card" style={{padding:'14px 16px'}}>
              <div style={{fontSize:12, color:'var(--text-3)', marginBottom:4}}>{label}</div>
              <div style={{fontSize:22, fontWeight:800, color}}>{val}</div>
            </div>
          ))}
        </div>

        {/* Filtres type + statut */}
        <div style={{display:'flex', gap:16, marginBottom:16, flexWrap:'wrap', alignItems:'center'}}>
          <div style={{display:'flex', gap:6}}>
            {[['all','Tous'],['gift','Bons cadeaux'],['avoir','Avoirs']].map(([val,label]) => (
              <button key={val} onClick={() => setTypeFilter(val)}
                style={{padding:'5px 14px', borderRadius:8, cursor:'pointer', fontFamily:'inherit', fontSize:13,
                  border:`1px solid ${typeFilter===val ? (val==='avoir'?'var(--amber)':'var(--accent)') :'var(--border)'}`,
                  background: typeFilter===val ? (val==='avoir'?'rgba(245,166,35,0.12)':'rgba(124,92,252,0.12)') : 'var(--bg-elevated)',
                  color: typeFilter===val ? (val==='avoir'?'var(--amber)':'var(--accent-light)') : 'var(--text-2)',
                  fontWeight: typeFilter===val ? 600 : 400}}>
                {val === 'avoir' ? '🎁 ' : ''}{label}
              </button>
            ))}
          </div>
          <div style={{width:1, height:20, background:'var(--border)'}}/>
          <div style={{display:'flex', gap:6}}>
            {[['all','Tous statuts'],['active','Actifs'],['used','Utilisés'],['expired','Expirés'],['cancelled','Annulés']].map(([val,label]) => (
              <button key={val} onClick={() => setFilter(val)}
                style={{padding:'5px 14px', borderRadius:8, border:`1px solid ${filter===val?'var(--accent)':'var(--border)'}`,
                  background: filter===val?'rgba(124,92,252,0.12)':'var(--bg-elevated)',
                  color: filter===val?'var(--accent-light)':'var(--text-2)',
                  cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:filter===val?600:400}}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="card">
          {filtered.length === 0 ? (
            <div className="empty-state"><p>Aucun bon cadeau</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Code</th><th>Bénéficiaire</th><th className="text-right">Valeur initiale</th>
                    <th className="text-right">Restant</th><th>Statut</th><th>Expire le</th><th>Créé par</th><th>Usages</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(gc => (
                    <tr key={gc.id}>
                      <td>
                        <span style={{fontFamily:'monospace', fontWeight:700, fontSize:13,
                          color:'var(--teal)', letterSpacing:'0.05em'}}>
                          {gc.code}
                        </span>
                      </td>
                      <td className="text-sm">
                        <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:2}}>
                          {(gc.gift_type === 'avoir') ? (
                            <span style={{fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:99,
                              background:'rgba(245,166,35,0.12)', color:'var(--amber)', letterSpacing:'0.04em'}}>
                              AVOIR
                            </span>
                          ) : (
                            <span style={{fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:99,
                              background:'rgba(124,92,252,0.1)', color:'var(--accent-light)', letterSpacing:'0.04em'}}>
                              BON CADEAU
                            </span>
                          )}
                        </div>
                        {gc.purchased_by_name || gc.customer_name || '—'}
                        {gc.purchased_by_email && <div style={{fontSize:11,color:'var(--text-3)'}}>{gc.purchased_by_email}</div>}
                        {gc.source_booking_ref && (
                          <div style={{marginTop:3}}>
                            <button onClick={() => navigate('/app/reservations/' + gc.source_booking_id)}
                              style={{background:'none', border:'none', cursor:'pointer', padding:0,
                                fontSize:11, color:'var(--accent-light)', textDecoration:'underline', fontFamily:'monospace'}}>
                              Résa {gc.source_booking_ref}
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="text-right bold">{parseFloat(gc.initial_amount).toFixed(2)} €</td>
                      <td className="text-right" style={{color: parseFloat(gc.remaining_amount)>0?'var(--teal)':'var(--text-3)', fontWeight:700}}>
                        {parseFloat(gc.remaining_amount).toFixed(2)} €
                      </td>
                      <td>
                        <span style={{fontSize:12, fontWeight:600, color:STATUS_COLOR[gc.status]}}>
                          ● {STATUS_LABEL[gc.status]}
                        </span>
                      </td>
                      <td className="text-sm text-muted">
                        {format(new Date(gc.expires_at), 'dd/MM/yyyy', {locale:fr})}
                      </td>
                      <td className="text-sm text-muted">
                        {gc.created_by_first} {gc.created_by_last}
                      </td>
                      <td>
                        {/* Annuler un usage */}
                        {(gc.usages||[]).filter(u=>u.amount).map((u,i) => (
                          <div key={i} style={{fontSize:11, color:'var(--amber)', display:'flex', alignItems:'center', gap:4}}>
                            -{parseFloat(u.amount_used||u.amount).toFixed(2)}€
                            {can('manager') && (
                              <button onClick={() => setConfirmAction({ type:'cancelUsage', usageId: u.id })}
                                style={{background:'none',border:'none',cursor:'pointer',padding:2}}>
                                <RotateCcw size={10} color="var(--amber)"/>
                              </button>
                            )}
                          </div>
                        ))}
                      </td>
                      <td>
                        {gc.status === 'active' && can('manager') && (
                          <button onClick={() => setConfirmAction({ type:'cancelCard', id: gc.id })}
                            style={{background:'none', border:'none', cursor:'pointer', padding:4}}>
                            <Trash2 size={13} color="var(--red)"/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showBulk && (
        <BulkModal onClose={() => setShowBulk(false)} onCreated={load}/>
      )}
      {showNew && (
        <NewGiftCardModal
          onClose={() => setShowNew(false)}
          onCreated={(gc) => { setCreated(gc); load(); }}
        />
      )}
      {created && <CreatedModal giftCard={created} onClose={() => setCreated(null)}/>}
      {confirmAction && (
        <ConfirmModal
          title={confirmAction.type === 'cancelCard' ? 'Annuler le bon cadeau' : "Annuler l'utilisation"}
          message={
            confirmAction.type === 'cancelCard'
              ? 'Annuler ce bon cadeau ?'
              : 'Annuler cette utilisation et recréditer le bon ?'
          }
          confirmLabel={confirmAction.type === 'cancelCard' ? 'Annuler le bon' : "Annuler l'usage"}
          danger
          loading={confirmBusy}
          onConfirm={runConfirmedAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </>
  );
}
